import { StarRatingDisplay } from './StarRating'

/**
 * Average rating + per-star breakdown bars. `breakdown` is whatever
 * computeBreakdownFromList()/the summary endpoint produced; `breakdownBasis`
 * says whether it's exact (from the API's own rating_breakdown) or only
 * covers the reviews currently loaded on this page — shown as a caption so
 * the bars are never presented as more precise than they are.
 */
export default function RatingSummary({ averageRating, totalReviews, breakdown, breakdownBasis, loading }) {
  if (loading) {
    return (
      <div className="animate-pulse space-y-3">
        <div className="h-10 w-24 rounded bg-stone-100" />
        <div className="h-3 w-32 rounded bg-stone-100" />
      </div>
    )
  }

  const maxCount = breakdown ? Math.max(1, ...Object.values(breakdown)) : 1

  return (
    <div className="flex flex-col sm:flex-row gap-6 sm:items-center">
      <div className="text-center sm:text-left shrink-0">
        <p className="font-cinzel text-4xl font-bold text-[#1C140A]">
          {totalReviews > 0 ? averageRating.toFixed(1) : '—'}
        </p>
        <div className="mt-1"><StarRatingDisplay value={averageRating} size="md" /></div>
        <p className="text-xs text-[#8B7355] mt-1">
          {totalReviews > 0 ? `${totalReviews.toLocaleString('en-IN')} review${totalReviews === 1 ? '' : 's'}` : 'No reviews yet'}
        </p>
      </div>

      {breakdown && totalReviews > 0 && (
        <div className="flex-1 min-w-0 space-y-1.5">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = breakdown[star] ?? 0
            const pct = Math.round((count / maxCount) * 100)
            return (
              <div key={star} className="flex items-center gap-2 text-xs">
                <span className="w-8 shrink-0 text-[#8B7355] font-medium">{star}★</span>
                <div className="flex-1 h-2 rounded-full bg-stone-100 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${pct}%`, background: 'linear-gradient(90deg,#C8922A,#E5A820)' }}
                  />
                </div>
                <span className="w-8 shrink-0 text-right text-[#8B7355]">{count}</span>
              </div>
            )
          })}
          {breakdownBasis === 'loaded' && (
            <p className="text-[10px] text-[#8B7355]/70 pt-1">Based on the reviews loaded below.</p>
          )}
        </div>
      )}
    </div>
  )
}
